import type { Virtualizer } from '@tanstack/react-virtual';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';

interface VirtualFocusOptions {
  totalItems: number;
  columnsPerRow: number;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
}

interface UseVirtualFocusResult {
  focusedIndex: number;
  setFocusedIndex: (index: number) => void;
  focusedRow: number;
  scrollToFocused: () => void;
  handleKeyDown: (
    event: React.KeyboardEvent,
    onSelect: (index: number) => void
  ) => void;
}

export function useVirtualFocus({
  totalItems,
  columnsPerRow,
  virtualizer,
}: VirtualFocusOptions): UseVirtualFocusResult {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const focusedRow = Math.floor(focusedIndex / columnsPerRow);

  const scrollToFocused = useCallback(() => {
    virtualizer.scrollToIndex(focusedRow, { align: 'center' });
  }, [virtualizer, focusedRow]);

  // Scroll when focus changes
  useEffect(() => {
    const visibleRange = virtualizer.range;
    if (
      visibleRange &&
      (focusedRow < visibleRange.startIndex ||
        focusedRow > visibleRange.endIndex)
    ) {
      scrollToFocused();
    }
  }, [focusedRow, virtualizer, scrollToFocused]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, onSelect: (index: number) => void) => {
      let newIndex = focusedIndex;

      switch (event.key) {
        case 'ArrowRight':
          event.preventDefault();
          newIndex = Math.min(focusedIndex + 1, totalItems - 1);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          newIndex = Math.max(focusedIndex - 1, 0);
          break;
        case 'ArrowDown':
          event.preventDefault();
          newIndex = Math.min(focusedIndex + columnsPerRow, totalItems - 1);
          break;
        case 'ArrowUp':
          event.preventDefault();
          newIndex = Math.max(focusedIndex - columnsPerRow, 0);
          break;
        case 'Enter':
        case ' ':
          event.preventDefault();
          onSelect(focusedIndex);
          return;
        case 'Home':
          event.preventDefault();
          newIndex = 0;
          break;
        case 'End':
          event.preventDefault();
          newIndex = totalItems - 1;
          break;
        default:
          return;
      }

      setFocusedIndex(newIndex);
    },
    [focusedIndex, totalItems, columnsPerRow]
  );

  return {
    focusedIndex,
    setFocusedIndex,
    focusedRow,
    scrollToFocused,
    handleKeyDown,
  };
}
